/**
 * Un input flotante para escribir una etiqueta encima del canvas: el nombre de
 * un contenedor, el texto de un conector.
 *
 * Es DOM puro sobre el canvas, hermano de las capas que dibujan esas etiquetas
 * cuando NO se están editando (`GroupLabels`, `EdgeLabels`). Los dos se turnan:
 * mientras este input vive, la etiqueta se oculta.
 *
 * No sabe nada del store: avisa el texto nuevo por callback y quien lo monta
 * decide qué hacer con él. El `className` es lo único que cambia entre usos —
 * cada etiqueta trae su propia caja en el CSS y el input tiene que calzar
 * exactamente donde estaba.
 */

export interface InlineInputCallbacks {
  /** El usuario confirmó un texto distinto del que había. */
  onCommit: (id: string, text: string) => void;
  /** Punto de anclaje en píxeles de pantalla. */
  anchorOf: (id: string) => { x: number; y: number } | null;
  /** Clase CSS del input, que define su caja y su tipografía. */
  className: string;
  /**
   * Desplazamiento vertical respecto del ancla, en píxeles de pantalla. El
   * nombre de un contenedor se sube sobre su borde; la etiqueta de un conector
   * va centrada en la línea.
   */
  offsetY?: number;
  /**
   * Escala con la que se dibuja el input, cuando la etiqueta que reemplaza no
   * está clavada al tamaño de pantalla. El nombre de un contenedor puede vivir
   * a escala de mundo: sin esto, abrir el editor lo haría saltar de tamaño.
   *
   * Dársela mueve el anclaje al `transform` —hay que componerla con `anchor`
   * y con `offsetY`—, así que quien la pasa tiene que pasar también el `anchor`
   * que su CSS traía. Sin ella manda el CSS, como siempre.
   */
  scaleOf?: (id: string) => number;
  /**
   * Cómo se ancla la caja a su punto, para poder reponerlo cuando `scaleOf`
   * obliga a escribir el `transform` a mano. Es el mismo valor que declara el
   * CSS de la clase (p. ej. `translateY(-100%)`: la caja cuelga hacia arriba).
   */
  anchor?: string;
  /**
   * Con qué medidas se estima el ancho de la caja: el ancho medio de un
   * carácter y el mínimo por debajo del cual no se achica, los dos en px de la
   * tipografía de la clase. Los valores por defecto son los de una etiqueta de
   * 11 px; el nombre de un contenedor, que se escribe a 16 px, necesita los
   * suyos o la caja queda corta desde la primera letra.
   */
  width?: { perChar: number; min: number };
}

export class InlineInput {
  private input: HTMLInputElement | null = null;
  private id: string | null = null;

  constructor(
    private host: HTMLElement,
    private cb: InlineInputCallbacks,
  ) {}

  /** Id de lo que se está editando, o null. */
  get editingId(): string | null {
    return this.id;
  }

  /** Abre el input sobre la etiqueta. */
  start(id: string, text: string): void {
    this.commit();

    const input = document.createElement("input");
    input.className = this.cb.className;
    input.value = text;
    input.spellcheck = false;
    this.id = id;
    this.input = input;
    this.syncRect();

    input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        this.commit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.cancel();
      }
    });
    input.addEventListener("blur", () => this.commit());
    // Que la caja acompañe al texto mientras se escribe.
    input.addEventListener("input", () => this.syncRect());
    // El pointerdown no debe llegar al canvas (deseleccionaría el grupo).
    input.addEventListener("pointerdown", (e) => e.stopPropagation());

    this.host.appendChild(input);
    input.focus();
    input.select();
  }

  /** Cierra guardando. Quien reciba el callback decide si el texto sirve. */
  commit(): void {
    const input = this.input;
    const id = this.id;
    this.input = null;
    this.id = null;
    if (!input || !id) return;
    input.remove();
    this.cb.onCommit(id, input.value.trim());
  }

  /** Cierra sin guardar. */
  cancel(): void {
    this.input?.remove();
    this.input = null;
    this.id = null;
  }

  /** Mantener el input pegado a la etiqueta mientras se hace pan/zoom. */
  syncRect(): void {
    const input = this.input;
    const id = this.id;
    if (!input || !id) return;
    // La etiqueta se oculta mientras se edita, así que su rectángulo ya no
    // existe: se ancla al mismo punto que usa ella.
    const p = this.cb.anchorOf(id);
    if (!p) return;
    const offsetY = this.cb.offsetY ?? 0;
    const scale = this.cb.scaleOf?.(id);
    input.style.left = `${Math.round(p.x)}px`;
    // Con escala propia, el aire respecto del ancla se mueve de `top` al
    // `transform`: adentro escala junto con la caja, y el input sigue calzando
    // sobre la etiqueta cuando el título es de tamaño de mundo. Sin ella, el
    // `top + offsetY` de siempre y el anclaje lo pone el CSS.
    if (scale === undefined) {
      input.style.top = `${Math.round(p.y) + offsetY}px`;
    } else {
      input.style.top = `${Math.round(p.y)}px`;
      input.style.transform = `scale(${scale}) ${this.cb.anchor ?? ""} translateY(${offsetY}px)`;
    }
    // El input no se ajusta solo al texto: se estima con el ancho medio de su
    // tipografía, con un mínimo para que nunca quede ilegible.
    const { perChar, min } = this.cb.width ?? { perChar: 6.4, min: 72 };
    input.style.width = `${Math.max(min, Math.round(input.value.length * perChar) + 24)}px`;
  }
}
